/**
 * Source-flip semantics for market segmentation fields.
 *
 * Shared between API and web — the client applies optimistically, the server
 * applies authoritatively. Keep identical to prevent UI-DB drift. See
 * docs/MARKET_SEGMENTATION_ARCHITECTURE.md §4.6.
 */

import type { SizingField, ScoreField } from '../types/marketSegmentation.js'

/**
 * Apply a user edit to a sizing field. Returns a new field with the correct
 * source:
 *   - 'edited' when overriding an existing AI value (aiValue != null && new != aiValue)
 *   - 'manual' otherwise (clearing to null, or entering a value that matches aiValue)
 */
export function applyUserEditSizing(current: SizingField, newValue: number | null): SizingField {
  const source: SizingField['source'] =
    current.aiValue != null && newValue !== null && newValue !== current.aiValue
      ? 'edited'
      : 'manual'
  return {
    ...current,
    value: newValue,
    source,
    editedAt: new Date().toISOString(),
  }
}

/** Same semantics for score fields (retains rationale + grounding metadata). */
export function applyUserEditScore(current: ScoreField, newValue: number | null): ScoreField {
  const source: ScoreField['source'] =
    current.aiValue != null && newValue !== null && newValue !== current.aiValue
      ? 'edited'
      : 'manual'
  return {
    ...current,
    value: newValue,
    source,
    editedAt: new Date().toISOString(),
  }
}

/** Revert to the AI-suggested value. Valid only when aiValue !== null. */
export function resetSizingToAI(current: SizingField): SizingField {
  return {
    ...current,
    value: current.aiValue,
    source: 'ai',
    editedAt: undefined,
  }
}

export function resetScoreToAI(current: ScoreField): ScoreField {
  return {
    ...current,
    value: current.aiValue,
    source: 'ai',
    editedAt: undefined,
  }
}

/**
 * Can the "reset to AI" action be shown for this field? Only meaningful when
 * there is an AI value to revert to AND the user has moved away from it.
 */
export function canResetToAI(field: SizingField | ScoreField): boolean {
  return field.aiValue != null && field.source !== 'ai'
}
